import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line } from 'recharts';
import PerformanceCard from './PerformanceCard';
import CircularGauge from './CircularGauge';
import AlertBadge from './AlertBadge';

const IncidentsRetardsSection = ({ data, onNavigate, formatCurrency }) => {
  const {
    incidents_jour = 0,
    incidents_mois = 0,
    operations_en_retard = 0,
    total_incidents,
    incidents_by_type,
    incidents_by_severity,
    average_resolution_time,
    incidents_trend,
    critical_routes,
    on_time_delivery_rate
  } = data;

  // Données pour le graphique des incidents par type
  const incidentsTypeData = Object.entries(incidents_by_type || {}).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
    color: getIncidentTypeColor(type)
  }));

  function getIncidentTypeColor(type) {
    const colors = {
      'Retard de chargement': '#EF4444',
      'Panne véhicule': '#DC2626',
      'Problème d\'accès au lieu de chargement': '#F59E0B',
      'Blocage administratif': '#8B5CF6',
      'Conditions météorologiques': '#10B981',
      'Contrôle routier prolongé': '#F97316',
      'autre': '#6B7280'
    };
    return colors[type] || '#6B7280';
  }

  // Données pour le graphique de tendance des incidents
  const incidentsTrendData = [
    { period: 'Il y a 3 mois', incidents: Math.max(0, total_incidents * 0.8) },
    { period: 'Il y a 2 mois', incidents: Math.max(0, total_incidents * 0.9) },
    { period: 'Mois dernier', incidents: Math.max(0, total_incidents * 1.1) },
    { period: 'Ce mois', incidents: total_incidents }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'élevée': return 'bg-red-100 text-red-800 border-red-200';
      case 'moyenne': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'faible': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'élevée': return '🚨';
      case 'moyenne': return '';
      case 'faible': return '💚';
      default: return '📋';
    }
  };

  return (
    <div className="space-y-8">
      {/* En-tête avec statistiques rapides */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">🚨</span>
          Incidents & Retards - Suivi en Temps Réel
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <AlertBadge
            type={incidents_jour > 2 ? "error" : incidents_jour > 0 ? "warning" : "success"}
            title="Incidents Aujourd'hui"
            description={`${incidents_jour} incident${incidents_jour > 1 ? 's' : ''} signalé${incidents_jour > 1 ? 's' : ''}`}
            count={incidents_jour}
            icon="🚨"
            pulse={incidents_jour > 2}
            onClick={() => onNavigate('incidents-today')}
          />
          
          <AlertBadge
            type={incidents_mois > 10 ? "error" : incidents_mois > 5 ? "warning" : "success"}
            title="Incidents ce Mois"
            description="Total mensuel"
            count={incidents_mois}
            icon="📊"
            onClick={() => onNavigate('incidents-month')}
          />
          
          <AlertBadge
            type={operations_en_retard > 5 ? "error" : operations_en_retard > 2 ? "warning" : "success"}
            title="Opérations en Retard"
            description="Dépassent délai prévu"
            count={operations_en_retard}
            icon="⏰"
            pulse={operations_en_retard > 5}
            onClick={() => onNavigate('operations-delayed')}
          />
          
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 text-center">
            <CircularGauge
              title="Ponctualité"
              value={on_time_delivery_rate || 85}
              max={100}
              size={100}
              color="#8B5CF6"
              unit="%"
            />
          </div>
        </div>

        {/* Tendance des incidents */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-900">Tendance des Incidents</h3>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              incidents_trend < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {incidents_trend >= 0 ? '+' : ''}{incidents_trend?.toFixed(1)}% ce mois
            </span>
          </div>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={incidentsTrendData}>
                <XAxis dataKey="period" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip 
                  formatter={(value) => [`${value} incident${value > 1 ? 's' : ''}`, 'Nombre']}
                  labelStyle={{ color: '#374151' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="incidents" 
                  stroke="#EF4444" 
                  strokeWidth={3}
                  dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Analyse détaillée des incidents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Types d'incidents */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">📊</span>
            Répartition par Type d'Incident
          </h3>
          
          {incidentsTypeData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incidentsTypeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({name, value}) => value > 0 ? `${name}: ${value}` : ''}
                    labelLine={false}
                  >
                    {incidentsTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <span className="text-4xl mb-2 block">✨</span>
                <p>Aucun incident enregistré</p>
              </div>
            </div>
          )}
        </div>

        {/* Gravité des incidents */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">⚡</span>
            Classification par Gravité
          </h3>
          
          <div className="space-y-4">
            {Object.entries(incidents_by_severity || {}).map(([severity, count]) => (
              <div key={severity} className={`p-4 rounded-lg border-2 ${getSeverityColor(severity)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getSeverityIcon(severity)}</span>
                    <div>
                      <div className="font-medium text-sm">
                        Gravité {severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </div>
                      <div className="text-xs opacity-75">
                        {severity === 'élevée' && 'Nécessite intervention immédiate'}
                        {severity === 'moyenne' && 'Suivi renforcé requis'}
                        {severity === 'faible' && 'Incidents mineurs'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{count}</div>
                    <div className="text-xs opacity-75">incident{count > 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Temps de résolution moyen */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏱️</span>
                <span className="font-medium text-blue-800">Temps de résolution moyen</span>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-blue-600">
                  {average_resolution_time?.toFixed(1)}h
                </div>
                <div className={`text-xs ${
                  average_resolution_time <= 24 ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {average_resolution_time <= 24 ? 'Excellent' : 'À améliorer'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Routes critiques */}
      {critical_routes?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">🛣️</span>
            Routes à Risque Élevé
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {critical_routes.map((route, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                route.risk_level === 'élevé' ? 'border-red-500 bg-red-50' :
                route.risk_level === 'moyen' ? 'border-yellow-500 bg-yellow-50' :
                'border-green-500 bg-green-50'
              }`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-sm text-gray-900">
                    {route.route}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    route.risk_level === 'élevé' ? 'bg-red-200 text-red-800' :
                    route.risk_level === 'moyen' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-green-200 text-green-800'
                  }`}>
                    {route.risk_level}
                  </span>
                </div>
                <div className="text-xs text-gray-600 mb-3">
                  {route.incidents} incident{route.incidents > 1 ? 's' : ''} 
                  {route.incident_rate && ` • ${route.incident_rate.toFixed(1)}% des opérations`}
                </div>
                <button 
                  onClick={() => onNavigate('route-details', { route: route.route })}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Analyser cette route →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions rapides */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">⚡</span>
          Actions Rapides
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('incident-report')}
            className="bg-red-500 text-white p-4 rounded-lg hover:bg-red-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">🚨</span>
            <span className="text-sm text-center">Signaler Incident</span>
          </button>
          
          <button
            onClick={() => onNavigate('operations-delayed')}
            className="bg-orange-500 text-white p-4 rounded-lg hover:bg-orange-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">⏰</span>
            <span className="text-sm text-center">Gérer Retards</span>
          </button>
          
          <button
            onClick={() => onNavigate('incidents-history')}
            className="bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">📊</span>
            <span className="text-sm text-center">Historique</span>
          </button>
          
          <button
            onClick={() => onNavigate('prevention-plan')}
            className="bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">🛡️</span>
            <span className="text-sm text-center">Plan Prévention</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentsRetardsSection;